import ImageInput from "../components/inputs/ImageInput";
import type { VoidComponent } from "solid-js";
import { createResource, createSignal } from "solid-js";
import type { ExtendableInputProps } from "../components/inputs/Input";
import type { CustomerResource, PendingCustomerResource } from "../services/CustomerResourceService";
import CustomerResourceService from "../services/CustomerResourceService";
import { useTranslation } from "../state/i18n";
import { isNil } from "lodash-es";
import type { MediaModalProps } from "../components/modal/MediaModal";

type CustomerResourceInputProps = {
  value: string | null;
  onSelect: (file: CustomerResource) => void;
  onClear: () => void;
  mediaModal?: Pick<MediaModalProps, "defaultTab">;
  buttonLabel?: string;
  fixedSize?: boolean;
} & ExtendableInputProps;

const CustomerResourceInput: VoidComponent<CustomerResourceInputProps> = (props) => {
  const { t } = useTranslation();
  const [progress, setProgress] = createSignal<number>(0);
  const [abortController, setAbortController] = createSignal<AbortController>();

  const [data, { mutate }] = createResource<CustomerResource | null, { value: string | null }>(
    () => ({ value: props.value }),
    async ({ value }) => {
      if (isNil(value)) {
        return null;
      }
      const secureUri = await CustomerResourceService.getSecureUri(value);

      return { id: value, source: secureUri } as CustomerResource;
    },
  );

  const onFileSelected = async (file: PendingCustomerResource) =>
    CustomerResourceService.upload(
      file,
      ({ progress }) => setProgress(!isNil(progress) ? progress * 100 : 0),
      setAbortController(new AbortController()).signal,
    );

  const onDelete = async (file: CustomerResource) => CustomerResourceService.delete(file.id);

  const onCancel = () => abortController()?.abort();

  return (
    <ImageInput
      {...props}
      buttonLabel={props.buttonLabel ?? t(($) => $.IMAGE_INPUT_LABEL)}
      label={props.label}
      info={props.info}
      onSelect={(file) => {
        mutate(file);
        props.onSelect(file);
      }}
      progress={progress()}
      setProgress={setProgress}
      onCancel={onCancel}
      onDelete={onDelete}
      fetchPage={(page, query) => CustomerResourceService.fetchPage(page, { query })}
      mediaUpload={{ onFileSelected }}
      defaultTab={props.mediaModal?.defaultTab}
      value={data()}
      onClear={() => {
        mutate(null);
        props.onClear();
      }}
    />
  );
};

export default CustomerResourceInput;
